import { useState, useMemo, useEffect } from "react";
import './FirmsPage.css';
import {
    TextField,
    Button,
    Container,
    styled,
    Box,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    Paper,
    InputAdornment,
} from "@mui/material";
import { Link } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import TableMenuButton from "components/button/TableMenuButton";
import FigmaModal from "components/modal/FigmaModal";
import { useLocation } from 'react-router-dom';
import { useDeleteFirmMutation, useGetFirmsQuery, useGetUserQuery } from '../../api/api';
import UploadButton from "components/button/UploadButton";
import EditFirmModal from "./EditFirmModal";
import { StyledTableCell, StyledTableRow, TableLoadingRow } from "components/tables/styledTableComponents";
import ErrorModal from "components/modal/ErrorModal";
import { getErrorMessage } from "lib/utils/getErrorMessage";
import MessageModal from "components/modal/MessageModal";

type ModalType = 'addFirm' | 'addFirmSuccess';

const downArrowPath = '/assets/icons/ArrowsDown.svg';
const upArrowPath = '/assets/icons/ArrowsUp.svg';
const upDownArrowPath = '/assets/icons/ArrowsDownUp.svg';

const FirmsPage = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState<ModalType>('addFirm');
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [searchTerm, setSearchTerm] = useState('');
    const [showImportAcknowledgement, setShowImportAcknowledgement] = useState<boolean>(false);
    const { isAuthenticated } = useAuth0();
    const location = useLocation();
    const { data: firms = [], error, isLoading } = useGetFirmsQuery();
    const [deleteFirm, { error: deleteError, reset: resetDeleteFirm }] = useDeleteFirmMutation();
    const { data: userData } = useGetUserQuery();

    const tableHeaderKeys = {
        name: 'Name',
        address: 'Address',
        city: 'City',
        subnational: 'State / Province / Region',
        country: 'Country',
        website: 'Website',
        firmAdmins: 'Firm Admins',
        firmUsers: 'Firm Users',
      }

    const filteredAndSortedData = useMemo(() => {
        let filteredItems = [...firms].filter((item) => {
          return (
            (item.name?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.address?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.city?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.subnational?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.country?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.zipcode?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
            (item.website?.toLowerCase().includes(searchTerm.toLowerCase()) || '')
          );
        });
    
        if (sortConfig !== null) {
          filteredItems.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
    
            // If they aren't the same type, convert them both to strings
            if (typeof aValue !== typeof bValue) {
              aValue = aValue ? aValue.toString() : '';
              bValue = bValue ? bValue.toString() : '';
            }
    
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return sortConfig.direction === 'ascending'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
            }
    
            if (aValue < bValue) {
              return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
              return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
          });
        }
        return filteredItems;
    }, [firms, searchTerm, sortConfig]);

    const handleSortTable = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (key) => {
        const arrowUp = <img src={upArrowPath} alt="Sort Ascending"></img>
        const arrowDown = <img src={downArrowPath} alt="Sort Descending"></img>
        const arrowUpDown = <img src={upDownArrowPath} alt="Sort"></img>

        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? arrowUp : arrowDown;
        }
        return arrowUpDown;
    };

    const handleEditFirm = (firm) => {
        setSelectedFirm(firm);
    };

    const generateWebLink = (url: string) => {
        // Adds https:// if protocol missing
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            url = 'https://' + url;
        }

        // Ensures www. is present if missing after protocol
        const protocolEndIndex = url.indexOf('//') + 2; // Find where the protocol ends
        if (!url.startsWith('www.', protocolEndIndex)) {
            url = url.slice(0, protocolEndIndex) + 'www.' + url.slice(protocolEndIndex);
        }

        return url;
    };

    const handleSuccessFirmImport = () => {
        setShowImportAcknowledgement(true);
    }

    // Modal functions
    const handleOpenModal = () => {
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        if (modalType === 'addFirmSuccess') {
            setModalType('addFirm' as ModalType);
        }
    }

    const handleCloseFirmModal = () => {
        setSelectedFirm(null);
        setIsModalOpen(false);
    }

    const handleSuccessFirmModal = () => {
        setModalType('updateFirm' as ModalType);
        setSelectedFirm(null);
        setIsModalOpen(true);
    }    

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const modal = queryParams.get('modal');
        const modalTypeParam = queryParams.get('modalType');

        if (modal === 'open' && modalTypeParam) {
            setIsModalOpen(true);
            if (modalTypeParam === 'addFirmSuccess') {
                setModalType(modalTypeParam as ModalType);
            }
        }
    }, [location]);

    const canUserDeleteFirms = ['Super Admin', 'Sub Super Admin'].includes(userData?.site_role ?? "Firm User")

    const tableHeaders = ['name', 'address', 'city', 'State / Province / Region', 'country', 'website', 'firmAdmins', 'firmUsers']

    return (
        <StyledContainer>
            <FigmaModal open={isModalOpen} onClose={handleCloseModal} type={modalType} values={{ firmName: location.state?.firmName ?? '', firmAdmin: location.state?.firmAdminName ?? ''}}/>
            <ErrorModal
                open={!!deleteError}
                onClose={resetDeleteFirm}
                errorMessage={deleteError ? getErrorMessage(deleteError) : null}
            />
            <EditFirmModal
                open={selectedFirm !== null}
                onClose={handleCloseFirmModal}
                onSuccess={handleSuccessFirmModal}
                firm={selectedFirm as any}
            />
            <Box sx={{
                display: 'flex'
            }}>
                <Button
                    type="button"
                    onClick={handleOpenModal}
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<AddIcon />}
                >
                    Add Firms
                </Button>
                <UploadButton type="firms" onSuccess={handleSuccessFirmImport}></UploadButton>
                <Button
                    component="a"
                    href="/assets/Firm_Import_Template_WIP.xlsx"
                    download
                    variant="outlined"
                    sx={{ mr: 2, borderRadius: 12 }}
                    startIcon={<ArticleIcon />}
                >
                    Download Import Template
                </Button>
            </Box>
            <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 20, paddingTop: 1, marginTop: 20 }}>
                <Box sx={{ mt: "30px", position: "relative" }}>
                    <TextField
                        placeholder="Search ..."
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            style: {
                                borderRadius: 22,
                                height: "44px",
                                width: "343px",
                                marginRight: "16px",
                            }
                        }}
                    />
                    <TableContainer component={Paper} sx={{ mt: "20px" }}>
                        <div style={{ width: '100%', height: '4px', background: 'linear-gradient(90deg, #059BD6 0%, #9FD64D 100%)' }} />
                        <Table>
                            <TableHead>
                                <StyledTableRow>
                                    {Object.entries(tableHeaderKeys).map(([key, title]) => (
                                    <StyledTableCell key={key} onClick={() => handleSortTable(key)} className={sortConfig.key === key ? 'sorted' : ''}>
                                        {/* Convert cammel case to readable */}
                                        {title.charAt(0).toUpperCase() + title.slice(1).replace(/([A-Z])/g, ' $1')}
                                        {key !== 'site_role' &&
                                        <span className="sort-icon">{getSortIcon(key)}</span>
                                        }
                                    </StyledTableCell>
                                    ))}
                                    <StyledTableCell sx={{
                                    position: 'sticky !important',
                                    right: 0,
                                    zIndex: 100,
                                    }}>
                                    <SettingsIcon sx={{
                                        display: 'flex',
                                        alignSelf: 'center'
                                    }} />
                                    </StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    isLoading ? <TableLoadingRow colSpan={tableHeaders.length + 1} /> :
                                    !isAuthenticated ? null : // needed since firms endpoint is open to unauthenticaed users
                                    filteredAndSortedData?.map((firm, index) => (
                                        <StyledTableRow key={index}>
                                            <StyledTableCell >{firm.name}</StyledTableCell >
                                            <StyledTableCell >{firm.address}</StyledTableCell >
                                            <StyledTableCell >{firm.city}</StyledTableCell >
                                            <StyledTableCell >{firm.subnational}</StyledTableCell >
                                            <StyledTableCell >{firm.country}</StyledTableCell >
                                            <StyledTableCell >
                                                <a href={generateWebLink(firm.website ?? "")} target="_blank" rel="noopener noreferrer">
                                                    {firm.website}
                                                </a>
                                            </StyledTableCell >
                                            <StyledTableCell>
                                                <Link to={`/firm-admin?firm=${encodeURIComponent(firm.name)}`} rel="noopener noreferrer">
                                                    {'View Firm Admins'}
                                                </Link>
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Link to={`/firm-users?firm=${encodeURIComponent(firm.name)}`} rel="noopener noreferrer">
                                                    {'View Firm Users'}
                                                </Link>
                                            </StyledTableCell>
                                            <StyledTableCell style={{ overflow: 'visible', position: 'sticky', right: 0, padding: 0 }}>
                                                <TableMenuButton
                                                    index={index}
                                                    tableContext="firms"
                                                    rowData={firm}
                                                    deleteMutation={deleteFirm}
                                                    disableDelete={!canUserDeleteFirms}
                                                    onEdit={() => handleEditFirm(firm)}
                                                />
                                            </StyledTableCell>
                                        </StyledTableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </div>
            <MessageModal
                open={showImportAcknowledgement}
                onClose={() => setShowImportAcknowledgement(false)}
                message={showImportAcknowledgement ? `Firms have been successfully imported.` : null}
                buttonText="Ok"
            />
        </StyledContainer>
    );
};

export default FirmsPage;

const StyledContainer = styled(Container)({
    marginBottom: 24,
});